import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import {
  ChatCircleText,
  CalendarBlank,
  PaperPlaneTilt,
  X,
  ShieldCheck,
  Plus,
  Trash,
  DotsSixVertical,
} from "@phosphor-icons/react";
import { publicShare } from "@/api";
import { toast } from "sonner";

const MIN_WIDTH = 340;
const MAX_WIDTH = 720;
const DEFAULT_WIDTH = 410;
const HEADER_HEIGHT = 64;
export default function PublicShareRightExchangeV2({
  token,
  senderName = "votre contact",
}) {
  const [open, setOpen] = useState(false);
  const [isCompactUi, setIsCompactUi] = useState(false);
  const [visualBottomInset, setVisualBottomInset] = useState(18);
  const [tab, setTab] = useState("messages");
  const [loading, setLoading] = useState(false);
  const [panelWidth, setPanelWidth] = useState(() => {
    const saved = Number(localStorage.getItem("publicShareExchangeWidth"));
    return saved || DEFAULT_WIDTH;
  });

  const [messages, setMessages] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [message, setMessage] = useState("");

  const hasUnreadMessages = messages.some(
    (item) =>
      item.senderType === "commercial" &&
      item.status !== "read"
  );

  const hasUnreadMeetings = meetings.some(
    (meeting) =>
      meeting.createdByType === "commercial" &&
      !meeting.publicReadAt
  );

  const [slots, setSlots] = useState([
    {
      id: "slot_1",
      date: "",
      start: "",
      durationMinutes: "30",
      locationType: "online",
      location: "",
    },
  ]);

  const loadExchange = async () => {
    if (!token) return;
    try {
      const [nextMessages, nextMeetings] = await Promise.all([
        publicShare.listExchangeMessages(token),
        publicShare.listExchangeMeetings(token),
      ]);
      setMessages(nextMessages || []);
      setMeetings(nextMeetings || []);
    } catch {}
  };

  useEffect(() => {
    loadExchange();
    const timer = setInterval(loadExchange, 15000);
    return () => clearInterval(timer);
  }, [token]);

  useEffect(() => {
    const updateViewportUi = () => {
      const coarsePointer =
        window.matchMedia?.("(pointer: coarse)")?.matches === true;

      const touchDevice =
        Number(navigator.maxTouchPoints || 0) > 0;

      const compactViewport = window.innerWidth < 1280;

      /*
       * Certains navigateurs Android peuvent exposer un viewport large
       * ou utiliser le mode site pour ordinateur tout en restant tactiles.
       */
      const compactTouchDevice =
        coarsePointer && touchDevice && window.innerWidth <= 1366;

      const visualViewport = window.visualViewport;

      /*
       * Espace occupé par les barres dynamiques du navigateur ou le clavier.
       * Cela évite que les boutons soient placés sous la barre Samsung/Safari.
       */
      const coveredBottom = visualViewport
        ? Math.max(
            0,
            window.innerHeight -
              visualViewport.height -
              visualViewport.offsetTop
          )
        : 0;

      setIsCompactUi(compactViewport || compactTouchDevice);
      setVisualBottomInset(
        Math.max(18, Math.round(coveredBottom + 18))
      );
    };

    updateViewportUi();

    window.addEventListener("resize", updateViewportUi);
    window.addEventListener("orientationchange", updateViewportUi);
    window.visualViewport?.addEventListener("resize", updateViewportUi);
    window.visualViewport?.addEventListener("scroll", updateViewportUi);

    return () => {
      window.removeEventListener("resize", updateViewportUi);
      window.removeEventListener("orientationchange", updateViewportUi);
      window.visualViewport?.removeEventListener(
        "resize",
        updateViewportUi
      );
      window.visualViewport?.removeEventListener(
        "scroll",
        updateViewportUi
      );
    };
  }, []);


  const startResize = (event) => {
    event.preventDefault();

    const startX = event.clientX;
    const startWidth = panelWidth;

    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";

    const onMove = (moveEvent) => {
      const delta = startX - moveEvent.clientX;
      const nextWidth = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, startWidth + delta));
      setPanelWidth(nextWidth);
      localStorage.setItem("publicShareExchangeWidth", String(nextWidth));
    };

    const onUp = () => {
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const markTabAsRead = async (nextTab) => {
    if (!token) return;

    try {
      if (nextTab === "messages") {
        await publicShare.markExchangeMessagesRead(token);

        setMessages((current) =>
          current.map((item) =>
            item.senderType === "commercial"
              ? {
                  ...item,
                  status: "read",
                  readAt:
                    item.readAt ||
                    new Date().toISOString(),
                }
              : item
          )
        );
      } else {
        await publicShare.markExchangeMeetingsRead(token);

        setMeetings((current) =>
          current.map((meeting) =>
            meeting.createdByType === "commercial"
              ? {
                  ...meeting,
                  publicReadAt:
                    meeting.publicReadAt ||
                    new Date().toISOString(),
                }
              : meeting
          )
        );
      }
    } catch {
      // Le panneau reste utilisable en cas d’échec.
    }
  };

  const openPanel = (nextTab) => {
    setTab(nextTab);
    setOpen(true);
    markTabAsRead(nextTab);
  };

  const changeTab = (nextTab) => {
    setTab(nextTab);
    markTabAsRead(nextTab);
  };

  const sendMessage = async () => {
    const content = message.trim();
    if (!content) return;

    setLoading(true);
    try {
      const created = await publicShare.sendExchangeMessage(token, { content });
      setMessages((current) => [...current, created]);
      setMessage("");
      toast.success("Message envoyé");
    } catch {
      toast.error("Message impossible à envoyer");
    } finally {
      setLoading(false);
    }
  };

  const addSlot = () => {
    if (slots.length >= 3) return;
    setSlots((current) => [
      ...current,
      {
        id: `slot_${Date.now()}`,
        date: "",
        start: "",
        durationMinutes: "30",
        locationType: "online",
        location: "",
      },
    ]);
  };

  const updateSlot = (id, key, value) => {
    setSlots((current) =>
      current.map((slot) => (slot.id === id ? { ...slot, [key]: value } : slot))
    );
  };

  const removeSlot = (id) => {
    if (slots.length <= 1) return;
    setSlots((current) => current.filter((slot) => slot.id !== id));
  };

  const proposeMeetings = async () => {
    const validSlots = slots
      .map((slot) => ({
        date: String(slot.date || "").trim(),
        start: String(slot.start || "").trim(),
        durationMinutes: Number(
          slot.durationMinutes || 30
        ),
        locationType: String(
          slot.locationType || "online"
        ).trim(),
        location: String(
          slot.location || ""
        ).trim(),
      }))
      .filter(
        (slot) =>
          slot.date &&
          slot.start &&
          slot.durationMinutes &&
          (
            slot.locationType !== "physical" ||
            slot.location
          )
      );

    if (!validSlots.length) {
      toast.error("Ajoutez au moins un rendez-vous complet");
      return;
    }

    setLoading(true);
    try {
      const created = await publicShare.proposeExchangeMeetings(token, { slots: validSlots });
      setMeetings((current) => [created, ...current]);
      setSlots([
        {
          id: "slot_1",
          date: "",
          start: "",
          durationMinutes: "30",
          locationType: "online",
          location: "",
        },
      ]);
      toast.success("Rendez-vous envoyé");
    } catch {
      toast.error("Rendez-vous impossible à envoyer");
    } finally {
      setLoading(false);
    }
  };

  const respondMeeting = async (
    meetingId,
    decision,
    selectedSlotIndex = null
  ) => {
    setLoading(true);

    try {
      const payload = { decision };

      if (
        decision === "accepted" &&
        selectedSlotIndex !== null
      ) {
        payload.selectedSlotIndex = selectedSlotIndex;
      }

      const updated =
        await publicShare.respondExchangeMeeting(
          token,
          meetingId,
          payload
        );

      setMeetings((current) =>
        current.map((meeting) =>
          meeting.id === meetingId
            ? updated
            : meeting
        )
      );

      toast.success(
        decision === "accepted"
          ? "Rendez-vous accepté"
          : "Proposition refusée"
      );
    } catch (error) {
      toast.error(
        error?.response?.data?.detail ||
        "Réponse impossible à enregistrer"
      );
    } finally {
      setLoading(false);
    }
  };

  const exchangeUi = (
    <>
      {!open && (
        <>
          {/* Actions mobiles : Samsung Internet, Chrome Android et Safari iOS */}
          <div
            className="fixed right-4 flex flex-col items-end gap-3"
            style={{
              display: isCompactUi ? "flex" : "none",
              bottom: `${visualBottomInset}px`,
              zIndex: 2147483000,
              maxWidth: "calc(100vw - 32px)",
              pointerEvents: "auto",
            }}
            data-testid="public-exchange-floating-actions-mobile"
          >
            <button
              type="button"
              onClick={() => openPanel("messages")}
              aria-label="Échanger avec votre contact"
              className="inline-flex h-12 items-center justify-center gap-2 rounded-full border border-[var(--do-border)] bg-[var(--do-accent)] px-4 text-sm font-black text-[var(--do-on-accent)] shadow-[var(--do-shadow-card)] transition hover:bg-[var(--do-accent-strong)] active:scale-[0.97]"
              data-testid="public-exchange-mobile-message"
            >
              <span className="relative inline-flex">
                <ChatCircleText size={20} weight="fill" />

                {hasUnreadMessages && (
                  <span
                    className="absolute -right-1.5 -top-1.5 h-3 w-3 rounded-full border-2 border-[var(--do-border-strong)] bg-[var(--do-accent-soft)]0"
                    data-testid="public-message-unread-dot-mobile"
                    aria-label="Nouveau message"
                  />
                )}
              </span>

              Échanger
            </button>

            <button
              type="button"
              onClick={() => openPanel("meeting")}
              aria-label="Prendre un rendez-vous"
              className="inline-flex h-12 items-center justify-center gap-2 rounded-full border border-[var(--do-border)] bg-[var(--do-accent)] px-4 text-sm font-black text-[var(--do-on-accent)] shadow-[var(--do-shadow-card)] transition hover:bg-[var(--do-accent-strong)] active:scale-[0.97]"
              data-testid="public-exchange-mobile-meeting"
            >
              <span className="relative inline-flex">
                <CalendarBlank size={20} weight="fill" />

                {hasUnreadMeetings && (
                  <span
                    className="absolute -right-1.5 -top-1.5 h-3 w-3 rounded-full border-2 border-[var(--do-border-strong)] bg-[var(--do-accent-soft)]0"
                    data-testid="public-meeting-unread-dot-mobile"
                    aria-label="Nouvelle proposition de rendez-vous"
                  />
                )}
              </span>

              Rendez-vous
            </button>
          </div>

          {/* Barre premium desktop */}
          <div
            className="fixed bottom-10 left-1/2 -translate-x-1/2 items-center gap-3 rounded-full border border-[var(--do-border)] bg-[var(--do-bg-elev)] p-3 shadow-[var(--do-shadow-card)] backdrop-blur-xl"
            style={{
              display: isCompactUi ? "none" : "flex",
              zIndex: 2147483000,
              pointerEvents: "auto",
            }}
            data-testid="public-exchange-floating-actions"
          >
            <div className="px-4">
              <div className="text-xs font-black text-[var(--do-ink)]">Besoin d’aide ?</div>
              <div className="text-[11px] text-[var(--do-muted)]">
                Échangez ou prenez rendez-vous sans quitter le document.
              </div>
            </div>

            <button
              type="button"
              onClick={() => openPanel("messages")}
              className="inline-flex items-center gap-2 rounded-full bg-[var(--do-accent-soft)] px-5 py-3 text-xs font-black text-[var(--do-accent)] hover:bg-[var(--do-accent-soft)]"
            >
              <span className="relative inline-flex">
                <ChatCircleText size={16} />

                {hasUnreadMessages && (
                  <span
                    className="absolute -right-1.5 -top-1.5 h-3 w-3 rounded-full border-2 border-[var(--do-border)] bg-[var(--do-accent-soft)]0"
                    data-testid="public-message-unread-dot"
                    aria-label="Nouveau message"
                  />
                )}
              </span>

              Échanger
            </button>

            <button
              type="button"
              onClick={() => openPanel("meeting")}
              className="inline-flex items-center gap-2 rounded-full bg-[var(--do-accent-soft)] px-5 py-3 text-xs font-black text-[var(--do-accent)] hover:bg-[var(--do-accent-soft)]"
            >
              <span className="relative inline-flex">
                <CalendarBlank size={16} />

                {hasUnreadMeetings && (
                  <span
                    className="absolute -right-1.5 -top-1.5 h-3 w-3 rounded-full border-2 border-[var(--do-border)] bg-[var(--do-accent-soft)]0"
                    data-testid="public-meeting-unread-dot"
                    aria-label="Nouvelle proposition de rendez-vous"
                  />
                )}
              </span>

              Prendre un RDV
            </button>
          </div>
        </>
      )}

      {open && (
        <aside
          style={{
            top: HEADER_HEIGHT,
            right: 0,
            bottom: 0,
            left: isCompactUi ? 0 : "auto",
            width: isCompactUi ? "100%" : `${panelWidth}px`,
            maxWidth: "100vw",
            zIndex: 2147483001,
            pointerEvents: "auto",
          }}
          className="fixed flex flex-col overflow-hidden overscroll-contain border-l border-[var(--do-border)] bg-[var(--do-bg-solid)] shadow-[var(--do-shadow-card)]"
          data-testid="public-share-exchange-v2"
        >
          <button
            type="button"
            onMouseDown={startResize}
            style={{
              display: isCompactUi ? "none" : "flex",
            }}
            className="absolute left-0 top-0 z-50 h-full w-4 -translate-x-1/2 cursor-col-resize items-center justify-center border-l border-[var(--do-border)] bg-[var(--do-accent-soft)] text-[var(--do-accent)] hover:bg-[var(--do-accent-soft)]"
            title="Faire glisser pour ajuster la largeur"
            data-testid="public-exchange-resize-handle"
          >
            <span className="flex h-10 w-10 items-center justify-center rounded-full border border-[var(--do-border)] bg-[var(--do-bg-solid)] shadow-sm">
              <DotsSixVertical size={18} weight="bold" />
            </span>
          </button>

          <div className="flex items-start justify-between gap-3 border-b border-[var(--do-border)] px-4 py-3 sm:px-6 sm:py-5">
            <div>
              <div className="text-[11px] font-black uppercase tracking-[0.16em] text-[var(--do-muted)]">
                {tab === "messages" ? "Conversation" : "Rendez-vous"}
              </div>
              <h2 className="mt-0.5 font-heading text-base font-black leading-tight text-[var(--do-ink)] sm:mt-1 sm:text-lg">
                {tab === "messages" ? "Échanger avec votre contact" : "Prendre un rendez-vous"}
              </h2>
              <p className="mt-1 text-[11px] leading-4 text-[var(--do-muted)] sm:text-xs sm:leading-5">
                {senderName} recevra votre demande.
              </p>
            </div>

            <button
              type="button"
              onClick={() => setOpen(false)}
              className="rounded-full border border-[var(--do-border)] p-2 text-[var(--do-muted)] hover:bg-[var(--do-bg-solid)]"
              data-testid="public-close-exchange-premium"
            >
              <X size={17} />
            </button>
          </div>

          <div className="grid grid-cols-2 gap-1.5 border-b border-[var(--do-border)] bg-[var(--do-bg-solid)] p-1.5 sm:gap-2 sm:p-2">
            <button
              type="button"
              onClick={() => changeTab("messages")}
              className={`rounded-xl px-3 py-2.5 text-sm font-black sm:rounded-2xl sm:px-4 sm:py-3 ${
                tab === "messages" ? "bg-[var(--do-bg-solid)] text-[var(--do-accent)] shadow-sm" : "text-[var(--do-muted)]"
              }`}
            >
              Messages
            </button>

            <button
              type="button"
              onClick={() => changeTab("meeting")}
              className={`rounded-xl px-3 py-2.5 text-sm font-black sm:rounded-2xl sm:px-4 sm:py-3 ${
                tab === "meeting" ? "bg-[var(--do-bg-solid)] text-[var(--do-accent)] shadow-sm" : "text-[var(--do-muted)]"
              }`}
            >
              Rendez-vous
            </button>
          </div>

          {tab === "messages" ? (
            <div className="flex min-h-0 flex-1 flex-col">
              <div className="flex-1 space-y-3 overflow-y-auto bg-gradient-to-b from-[var(--do-accent-strong)] to-[var(--do-accent-2)] px-4 py-4 sm:space-y-4 sm:px-5 sm:py-5">
                {messages.length === 0 ? (
                  <div className="rounded-3xl border border-dashed border-[var(--do-border)] bg-[var(--do-bg-solid)] p-5 text-sm leading-6 text-[var(--do-muted)]">
                    Posez votre question ici.
                  </div>
                ) : (
                  messages.map((item) => {
                    const isProspect = item.senderType !== "commercial";
                    return (
                      <div key={item.id} className={`flex ${isProspect ? "justify-start" : "justify-end"}`}>
                        <div className={`max-w-[82%] rounded-3xl px-4 py-3 text-sm shadow-sm ${
                          isProspect ? "bg-[var(--do-accent-soft)] text-[var(--do-ink)]" : "border border-[var(--do-border)] bg-[var(--do-bg-solid)] text-[var(--do-ink)]"
                        }`}>
                          {item.content || item.message}
                          <div className={`mt-1 text-[10px] text-[var(--do-muted)] ${isProspect ? "" : "text-right"}`}>
                            {isProspect ? "Vous" : item.authorName || senderName}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              <div className="border-t border-[var(--do-border)] bg-[var(--do-bg-solid)] p-3 sm:p-4">
                <div className="flex gap-2">
                  <input
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") sendMessage();
                    }}
                    placeholder="Écrivez votre message..."
                    className="min-w-0 flex-1 rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-2.5 text-base outline-none focus:border-[var(--do-border-strong)] focus:bg-[var(--do-bg-solid)] sm:rounded-2xl sm:px-4 sm:py-3 sm:text-sm"
                  />
                  <button
                    type="button"
                    onClick={sendMessage}
                    disabled={loading || !message.trim()}
                    className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[var(--do-accent)] text-[var(--do-on-accent)] hover:bg-[var(--do-accent-strong)] disabled:bg-[var(--do-bg-2)] sm:h-12 sm:w-12 sm:rounded-2xl"
                  >
                    <PaperPlaneTilt size={20} weight="fill" />
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto bg-gradient-to-b from-[var(--do-accent-strong)] to-[var(--do-accent-2)] p-4 sm:p-5">
              <div className="mb-3 rounded-2xl border border-[var(--do-border)] bg-[var(--do-accent-soft)] p-3 sm:mb-4 sm:rounded-3xl sm:p-4">
                <div className="flex gap-3">
                  <ShieldCheck
                    size={20}
                    weight="fill"
                    className="text-[var(--do-accent)]"
                  />
                  <p className="text-xs leading-5 text-[var(--do-ink)]">
                    Acceptez ou refusez une proposition de votre
                    contact, ou transmettez vos disponibilités.
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                {meetings.length === 0 && (
                  <div className="rounded-3xl border border-dashed border-[var(--do-border)] bg-[var(--do-bg-solid)] p-5 text-sm text-[var(--do-muted)]">
                    Aucun rendez-vous proposé pour le moment.
                  </div>
                )}

                {meetings.map((meeting) => {
                  const commercialProposal =
                    meeting.createdByType === "commercial";

                  const accepted =
                    meeting.status === "accepted" ||
                    meeting.status === "selected";

                  const refused =
                    meeting.status === "refused";

                  return (
                    <div
                      key={meeting.id}
                      className="rounded-3xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] p-4 shadow-sm"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <h3 className="text-sm font-black text-[var(--do-ink)]">
                            {commercialProposal
                              ? "Proposition de rendez-vous"
                              : "Vos disponibilités envoyées"}
                          </h3>

                          <p className="mt-1 text-xs text-[var(--do-muted)]">
                            {accepted
                              ? meeting.status === "selected"
                                ? "Rendez-vous confirmé"
                                : "Accepté · en attente de confirmation"
                              : refused
                                ? "Proposition refusée"
                                : commercialProposal
                                  ? "En attente de votre réponse"
                                  : "En attente de votre contact"}
                          </p>
                        </div>

                        {accepted && (
                          <span className="rounded-full bg-[var(--do-accent-soft)] px-3 py-1 text-[11px] font-black text-[var(--do-accent)]">
                            Accepté
                          </span>
                        )}

                        {refused && (
                          <span className="rounded-full bg-[var(--do-accent-soft)] px-3 py-1 text-[11px] font-black text-[var(--do-accent)]">
                            Refusé
                          </span>
                        )}
                      </div>

                      <div className="mt-4 space-y-3">
                        {(meeting.slots || []).map(
                          (slot, index) => {
                            const selected =
                              meeting.selectedSlotIndex === index;

                            return (
                              <div
                                key={`${meeting.id}-${index}`}
                                className={`rounded-2xl border px-4 py-3 text-sm ${
                                  selected && accepted
                                    ? "border-[var(--do-border)] bg-[var(--do-accent-soft)]"
                                    : "border-[var(--do-border)] bg-[var(--do-bg-solid)]"
                                }`}
                              >
                                <div className="font-black text-[var(--do-ink)]">
                                  {slot.date}
                                </div>

                                <div className="mt-1 text-xs text-[var(--do-muted)]">
                                  {slot.start}
                                  {" · "}
                                  {slot.durationMinutes
                                    ? `${slot.durationMinutes} min`
                                    : slot.end
                                      ? `jusqu’à ${slot.end}`
                                      : "30 min"}
                                </div>

                                {slot.location && (
                                  <div className="mt-2 break-words text-xs text-[var(--do-muted)]">
                                    <span className="font-black">
                                      {slot.locationType === "physical"
                                        ? "Adresse : "
                                        : "Lieu / connexion : "}
                                    </span>
                                    {slot.location}
                                  </div>
                                )}

                                {commercialProposal &&
                                  meeting.status === "pending" && (
                                    <button
                                      type="button"
                                      onClick={() =>
                                        respondMeeting(
                                          meeting.id,
                                          "accepted",
                                          index
                                        )
                                      }
                                      disabled={loading}
                                      className="mt-3 w-full rounded-xl bg-[var(--do-accent)] px-3 py-2 text-xs font-black text-[var(--do-on-accent)] hover:bg-[var(--do-accent-strong)] disabled:bg-[var(--do-bg-2)]"
                                      data-testid="public-meeting-accept-action"
                                    >
                                      Accepter ce créneau
                                    </button>
                                  )}
                              </div>
                            );
                          }
                        )}
                      </div>

                      {commercialProposal &&
                        meeting.status === "pending" && (
                          <button
                            type="button"
                            onClick={() =>
                              respondMeeting(
                                meeting.id,
                                "refused"
                              )
                            }
                            disabled={loading}
                            className="mt-3 w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-2 text-xs font-black text-[var(--do-accent)] hover:bg-[var(--do-accent-soft)] disabled:text-[var(--do-muted)]"
                            data-testid="public-meeting-refuse-action"
                          >
                            Refuser la proposition
                          </button>
                        )}
                    </div>
                  );
                })}

                <div className="rounded-3xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] p-4 shadow-sm">
                  <h3 className="text-sm font-black text-[var(--do-ink)]">
                    Proposer vos disponibilités
                  </h3>

                  <div className="mt-4 space-y-3">
                    {slots.map((slot, index) => (
                      <div
                        key={slot.id}
                        className="rounded-2xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] p-3"
                      >
                        <div className="mb-3 flex items-center justify-between">
                          <div className="text-xs font-black uppercase tracking-wide text-[var(--do-muted)]">
                            Rendez-vous {index + 1}
                          </div>

                          {slots.length > 1 && (
                            <button
                              type="button"
                              onClick={() =>
                                removeSlot(slot.id)
                              }
                              className="text-[var(--do-accent)]"
                            >
                              <Trash size={15} />
                            </button>
                          )}
                        </div>

                        <div className="grid min-w-0 gap-2 sm:grid-cols-2">
                          <label className="min-w-0 space-y-1">
                            <span className="text-[11px] font-black text-[var(--do-muted)]">
                              Date
                            </span>

                            <input
                              type="date"
                              value={slot.date}
                              onChange={(event) =>
                                updateSlot(
                                  slot.id,
                                  "date",
                                  event.target.value
                                )
                              }
                              className="min-w-0 max-w-full w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-2 text-base sm:text-sm"
                            />
                          </label>

                          <label className="min-w-0 space-y-1">
                            <span className="text-[11px] font-black text-[var(--do-muted)]">
                              Heure
                            </span>

                            <input
                              type="time"
                              value={slot.start}
                              onChange={(event) =>
                                updateSlot(
                                  slot.id,
                                  "start",
                                  event.target.value
                                )
                              }
                              className="min-w-0 max-w-full w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-2 text-base sm:text-sm"
                            />
                          </label>

                          <label className="min-w-0 space-y-1">
                            <span className="text-[11px] font-black text-[var(--do-muted)]">
                              Durée
                            </span>

                            <select
                              value={slot.durationMinutes}
                              onChange={(event) =>
                                updateSlot(
                                  slot.id,
                                  "durationMinutes",
                                  event.target.value
                                )
                              }
                              className="min-w-0 max-w-full w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-2 text-base sm:text-sm"
                            >
                              <option value="15">15 minutes</option>
                              <option value="30">30 minutes</option>
                              <option value="45">45 minutes</option>
                              <option value="60">1 heure</option>
                              <option value="90">1 h 30</option>
                              <option value="120">2 heures</option>
                            </select>
                          </label>

                          <label className="min-w-0 space-y-1">
                            <span className="text-[11px] font-black text-[var(--do-muted)]">
                              Format
                            </span>

                            <select
                              value={slot.locationType}
                              onChange={(event) =>
                                updateSlot(
                                  slot.id,
                                  "locationType",
                                  event.target.value
                                )
                              }
                              className="min-w-0 max-w-full w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-2 text-base sm:text-sm"
                            >
                              <option value="online">
                                En ligne
                              </option>

                              <option value="physical">
                                Sur place
                              </option>
                            </select>
                          </label>
                        </div>

                        {slot.locationType === "physical" ? (
                          <label className="mt-2 block min-w-0 space-y-1">
                            <span className="text-[11px] font-black text-[var(--do-muted)]">
                              Adresse du rendez-vous
                            </span>

                            <input
                              type="text"
                              value={slot.location}
                              onChange={(event) =>
                                updateSlot(
                                  slot.id,
                                  "location",
                                  event.target.value
                                )
                              }
                              placeholder="Adresse complète"
                              required
                              className="min-w-0 max-w-full w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-2 text-base sm:text-sm"
                              data-testid="public-meeting-physical-address"
                            />
                          </label>
                        ) : (
                          <div
                            className="mt-2 rounded-xl border border-[var(--do-border)] bg-[var(--do-accent-soft)] px-3 py-2 text-xs text-[var(--do-accent)]"
                            data-testid="public-meeting-online-no-link-required"
                          >
                            Aucun lien n’est nécessaire pour proposer ce créneau en ligne.
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {slots.length < 3 && (
                    <button
                      type="button"
                      onClick={addSlot}
                      className="mt-3 inline-flex items-center gap-2 text-sm font-black text-[var(--do-accent)]"
                    >
                      <Plus size={16} />
                      Ajouter un rendez-vous
                    </button>
                  )}

                  <button
                    type="button"
                    onClick={proposeMeetings}
                    disabled={loading}
                    className="mt-5 w-full rounded-2xl bg-[var(--do-accent)] py-3 text-sm font-black text-[var(--do-on-accent)] hover:bg-[var(--do-accent-strong)] disabled:bg-[var(--do-bg-2)]"
                  >
                    Envoyer mes disponibilités
                  </button>
                </div>
              </div>
            </div>
          )}
        </aside>
      )}
    </>
  );

  return typeof document !== "undefined"
    ? createPortal(exchangeUi, document.body)
    : exchangeUi;
}
